import { __assign, __makeTemplateObject } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, memo, useMemo } from 'react';
import clipboardCopy from 'clipboard-copy';
import styled from 'styled-components';
import pagesData from '../../data/pages-v2.json';
import { getLinesByVerses, getValidPageNumber } from '../../utils/page';
import Basmalah from '../Basmalah';
import Line from './Line';
import SurahTitle from './SurahTitle';
import { CENTERED_PAGES_VERTICAL, SURAH_ATTAWBAH_ID } from '../../commons/constants';
import { isDeepEqual } from '../../utils/array';
var ViewContainer = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    width: 100%;\n    container-type: inline-size;\n    direction: rtl !important;\n\n    * {\n        direction: rtl !important;\n    }\n\n    ", "\n"], ["\n    width: 100%;\n    container-type: inline-size;\n    direction: rtl !important;\n\n    * {\n        direction: rtl !important;\n    }\n\n    ", "\n"])), function (_a) {
    var $fixedAspectRatio = _a.$fixedAspectRatio, $page = _a.$page;
    return $fixedAspectRatio ? 'aspect-ratio: 1/1.32;' : $page <= 2 && 'padding-bottom: 3cqi;';
});
var View = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    width: 100%;\n    height: 100%;\n    display: flex;\n    flex-direction: column;\n    justify-content: ", ";\n    align-items: stretch;\n    padding: 1cqi 0;\n\n    text-align: justify;\n\n    & > .surah-title:first-child {\n        // margin-top: 0;\n        margin-top: -1cqi !important;\n        border-top-width: 0 !important;\n    }\n"], ["\n    width: 100%;\n    height: 100%;\n    display: flex;\n    flex-direction: column;\n    justify-content: ", ";\n    align-items: stretch;\n    padding: 1cqi 0;\n\n    text-align: justify;\n\n    & > .surah-title:first-child {\n        // margin-top: 0;\n        margin-top: -1cqi !important;\n        border-top-width: 0 !important;\n    }\n"
    /**
     * Used to center surah in the first 2 pages of the mushaf
     */
])), function (props) { return (props.$center ? 'center' : 'inherit'); });
/**
 * Used to center surah in the first 2 pages of the mushaf
 */
var FlexDiv = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n    flex: 1;\n    flex-grow: 3;\n"], ["\n    flex: 1;\n    flex-grow: 3;\n"
    /**
     * Handles copy text event
     * - remove line break after words. this is an issue on Chrome.
     */
])));
/**
 * Handles copy text event
 * - remove line break after words. this is an issue on Chrome.
 */
var onQuranTextCopy = function (event) {
    var selection = document.getSelection();
    var quranWordsToCopy = Array.from(document.querySelectorAll("[data-word]"))
        .filter(function (node) { return selection === null || selection === void 0 ? void 0 : selection.containsNode(node, true); })
        .map(function (wordElement) { return wordElement.innerHTML.trim(); })
        .join(' ');
    // .replace('&nbsp;', '')
    if (typeof quranWordsToCopy == 'string' && (quranWordsToCopy === null || quranWordsToCopy === void 0 ? void 0 : quranWordsToCopy.length) > 0) {
        event.preventDefault();
        clipboardCopy(quranWordsToCopy);
    }
};
/**
 * Renders a page of quran
 */
var ReadingView = memo(function ReadingView(_a) {
    var _b = _a.page, page = _b === void 0 ? 1 : _b, _c = _a.readingViewStyles, readingViewStyles = _c === void 0 ? {} : _c, _d = _a.surahTitleStyles, surahTitleStyles = _d === void 0 ? {} : _d, _e = _a.fixedAspectRatio, fixedAspectRatio = _e === void 0 ? true : _e;
    var _f = useMemo(function () {
        var pageNumber = getValidPageNumber(page);
        var pageVerses = pagesData[pageNumber - 1];
        var pageLines = Object.values(getLinesByVerses(pageVerses));
        return [pageNumber, pageLines];
    }, [page]), pageNumber = _f[0], pageLines = _f[1];
    var styles = useMemo(function () { return (__assign(__assign({ width: '440px' }, readingViewStyles), { overflow: 'visible' })); }, [readingViewStyles]);
    var viewContainerStyleProps = useMemo(function () { return ({
        $fixedAspectRatio: fixedAspectRatio,
        $page: pageNumber,
    }); }, [fixedAspectRatio, pageNumber]);
    var viewStyleProps = useMemo(function () { return ({
        $center: CENTERED_PAGES_VERTICAL.includes(pageNumber),
    }); }, [pageNumber]);
    return (_jsx(ViewContainer, __assign({ style: styles }, viewContainerStyleProps, { children: _jsxs(View, __assign({ onCopy: onQuranTextCopy }, viewStyleProps, { children: [pageLines.map(function (words, lineIndex, _a) {
                    var length = _a.length;
                    var _b = words[0], isStartOfSurah = _b.is_start, surahId = _b.chapter_id;
                    var isSurahAttawbah = surahId === SURAH_ATTAWBAH_ID;
                    var isFirstPage = pageNumber === 1;
                    /**
                     * adds basmalah if these conditions are met:
                     * - start of the surah (chapter).
                     * - not the 1st page because it already has basmalah; which is the first ayah (verse).
                     * - the Surah is not Al-Tawbah; explaination:
                     *      - [Shaykh Abdul-Aziz Ibn Baz](https://binbaz.org.sa/fatwas/7171/%D9%85%D8%A7-%D8%B3%D8%A8%D8%A8-%D8%B9%D8%AF%D9%85-%D9%88%D8%AC%D9%88%D8%AF-%D8%A7%D9%84%D8%A8%D8%B3%D9%85%D9%84%D8%A9-%D9%81%D9%8A-%D8%B3%D9%88%D8%B1%D8%A9-%D8%A7%D9%84%D8%AA%D9%88%D8%A8%D8%A9)
                     *      - [Shaykh Othman Alkamees](https://youtu.be/P75vmy6YCzg)
                     */
                    var shouldAddBasmalah = isStartOfSurah && !isFirstPage && !isSurahAttawbah;
                    var lineNumber = lineIndex + 1;
                    return (_jsxs(Fragment, { children: [isStartOfSurah && (_jsx(SurahTitle, { linesLength: length, surahNumber: words[0].chapter_id, surahTitleStyles: surahTitleStyles })), viewStyleProps.$center && lineNumber === 1 && _jsx(FlexDiv, {}), shouldAddBasmalah && (_jsx(Basmalah, { className: "react-quran_basmalah", height: "7cqi", style: { marginBottom: '1cqi' } })), _jsx(Line, { page: pageNumber, words: words, surahId: surahId, lineNumber: lineNumber })] }, "page".concat(page, "-line").concat(lineNumber)));
                }), viewStyleProps.$center && _jsx(FlexDiv, {})] })) })));
}, isDeepEqual);
export default ReadingView;
var templateObject_1, templateObject_2, templateObject_3;
